import * as XLSX from 'xlsx';

export interface ParsedExcelData {
  columns: string[];
  rows: Record<string, any>[];
}

export interface GcsTableData {
  columns: string[];
  rows: Array<{ data: any[] }>;
}

export interface ParsedGcsTablePayload {
  filePurpose: string;
  fileName: string;
  gcsTable: GcsTableData[];
}

/**
 * Parse an Excel file and extract columns and rows
 * @param file - The Excel file to parse
 * @returns Parsed data with columns and rows
 */
export const parseExcelFile = async (file: File): Promise<ParsedExcelData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        }) as any[][];

        if (jsonData.length === 0) {
          resolve({ columns: [], rows: [] });
          return;
        }

        // First row is headers
        const columns = jsonData[0] as string[];

        // Remaining rows are data - include __rowNumber for tracking
        const rows = jsonData.slice(1).map((row: any[], rowIdx) => {
          const rowObj: any = {
            __rowNumber: rowIdx + 1, // Data row number (starts at 1)
            RowNumber: rowIdx + 1,
          };
          columns.forEach((col, idx) => {
            rowObj[col] = row[idx] !== undefined ? row[idx] : '';
          });
          return rowObj;
        });

        resolve({ columns, rows });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Parse Excel file and convert to the same gcsTable format used by the API.
 * This keeps local parsing consistent with API responses.
 */
export const parseExcelFileToGcsTable = async (
  file: File,
  filePurpose: string
): Promise<ParsedGcsTablePayload> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        }) as any[][];

        if (jsonData.length === 0) {
          resolve({
            filePurpose,
            fileName: file.name,
            gcsTable: [{ columns: [], rows: [] }],
          });
          return;
        }

        const columns = (jsonData[0] as string[]).map((c) => String(c || '').trim());
        
        // Add RowNumber as first column if not present
        const hasRowNumberCol = columns.some(c => c.toLowerCase() === 'rownumber');
        const finalColumns = hasRowNumberCol ? columns : ['RowNumber', ...columns];
        
        const rows = jsonData.slice(1).map((row: any[], rowIdx) => ({
          // Match API gcsTable row structure
          // If we added RowNumber column, prepend the row number (index + 2 for Excel)
          data: hasRowNumberCol 
            ? columns.map((_, colIdx) => (row[colIdx] !== undefined ? row[colIdx] : ''))
            : [rowIdx + 1, ...columns.map((_, colIdx) => (row[colIdx] !== undefined ? row[colIdx] : ''))],
        }));

        resolve({
          filePurpose,
          fileName: file.name,
          gcsTable: [{ columns: finalColumns, rows }],
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Export data to Excel file
 * @param data - Data with columns and rows
 * @param filename - Output filename
 */
export const exportToExcel = (
  data: ParsedExcelData,
  filename: string = 'export.xlsx'
): void => {
  const worksheet = XLSX.utils.json_to_sheet(data.rows, {
    header: data.columns,
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, filename);
};
